import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const HandicapGuidePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const playerToken = searchParams.get('token');

  const Section = ({ icon, title, children }) => (
    <div style={{
      background: '#fff',
      borderRadius: 16,
      padding: 24,
      marginBottom: 24,
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div style={{
          width: 40,
          height: 40,
          background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20
        }}>
          {icon}
        </div>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#1f2937' }}>{title}</h2>
      </div>
      <div style={{ color: '#4b5563', lineHeight: 1.7 }}>
        {children}
      </div>
    </div>
  );

  const Highlight = ({ children }) => (
    <span style={{ 
      background: '#d1fae5', 
      padding: '2px 8px', 
      borderRadius: 4, 
      fontWeight: 600,
      color: '#065f46'
    }}>
      {children}
    </span>
  );

  const CheckItem = ({ children }) => (
    <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
      <span style={{ color: '#059669', fontSize: 18 }}>✓</span>
      <span>{children}</span>
    </div>
  );

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 50%, #f0fdf4 100%)'
    }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: '20px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        boxShadow: '0 4px 12px rgba(45, 106, 79, 0.2)'
      }}>
        <div style={{ 
          maxWidth: 800, 
          margin: '0 auto', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48,
              height: 48,
              background: '#fff',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 6
            }}>
              <img src="/logo.png" alt="TTO" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#fff' }}>Understanding Handicaps</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>Without falling asleep 😴</div>
            </div>
          </div>
          <button
            onClick={() => {
              if (playerToken) {
                navigate(`/portal?token=${playerToken}`);
              } else {
                navigate('/?from=guide');
              }
            }}
            style={{
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              border: '2px solid rgba(255,255,255,0.3)',
              padding: '8px 16px',
              borderRadius: 8,
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14
            }}
          >
            {playerToken ? '← My Portal' : '← Home'}
          </button>
        </div>
      </header>

      {/* Content */}
      <main style={{ maxWidth: 800, margin: '0 auto', padding: '32px 20px 60px' }}>
        
        {/* Hero intro */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          marginBottom: 32,
          textAlign: 'center',
          border: '1px solid #e5e7eb',
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>🏌️‍♂️</div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: '#1f2937', marginBottom: 12 }}>
            The Handicap System: What You Need to Know
          </h1>
          <p style={{ fontSize: 18, color: '#6b7280', lineHeight: 1.6, maxWidth: 600, margin: '0 auto' }}>
            Golf is more fun when everyone has a fighting chance — and that's exactly why the handicap system exists.
          </p>
        </div>

        <Section icon="🎯" title="Why Do Handicaps Exist?">
          <p style={{ marginBottom: 16 }}>
            Golf is… well, golf. Nobody plays it the exact same way. Some of us bomb it, some of us bunt it, 
            some of us chip it twice and putt it three times. Without a handicap, matches would get lopsided fast.
          </p>
          <p style={{ marginBottom: 16 }}>
            The <Highlight>USGA Handicap System</Highlight> (now part of the World Handicap System) exists to:
          </p>
          <CheckItem>Level the playing field</CheckItem>
          <CheckItem>Allow players of different abilities to compete</CheckItem>
          <CheckItem>Create fair matches and tournaments</CheckItem>
          <CheckItem>Track improvement over time</CheckItem>
          <CheckItem>Make the game more enjoyable for everyone</CheckItem>
          <p style={{ marginTop: 16, fontStyle: 'italic', color: '#059669' }}>
            It's one of the few sports where the 20-handicap player can legitimately beat the 2-handicap player — 
            and nobody has to feel weird about it.
          </p>
        </Section>

        <Section icon="📝" title="Why Posting Scores Actually Matters">
          <p style={{ marginBottom: 16 }}>
            If you've ever heard someone say <em>"I only post my good rounds,"</em> you've just met a 
            walking compliance problem. Handicaps rely on <Highlight>all scores</Highlight>, good and bad.
          </p>
          <p style={{ marginBottom: 16 }}>
            Not posting all your scores makes your handicap less accurate, which creates two problems:
          </p>
          <div style={{ 
            background: '#fef3c7', 
            padding: 16, 
            borderRadius: 12, 
            marginBottom: 16,
            border: '1px solid #fcd34d'
          }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>⚠️ The Two Pitfalls:</div>
            <div style={{ marginBottom: 4 }}>• <strong>Too low</strong> (sandbagging by accident or design)</div>
            <div>• <strong>Too high</strong> (vanity index)</div>
          </div>
          <p>
            Both are bad for competition, but "only posting good rounds" is actually the fastest way to create 
            an artificially low index — which means you'll be spotting strokes you don't deserve.
            <br/><br/>
            <strong>Posting everything</strong> makes the math honest, the matches fair, and keeps your playing partners happy.
          </p>
        </Section>

        <Section icon="⏳" title="When Should You Post?">
          <p style={{ marginBottom: 16 }}>
            Under the World Handicap System, scores should be posted <Highlight>the day you play</Highlight>, because:
          </p>
          <CheckItem>Course Ratings update</CheckItem>
          <CheckItem>Slope Ratings are applied</CheckItem>
          <CheckItem>Playing Conditions Calculation (PCC) adjusts for conditions</CheckItem>
          <p style={{ 
            background: '#dbeafe', 
            padding: 16, 
            borderRadius: 12, 
            marginTop: 16,
            border: '1px solid #93c5fd'
          }}>
            💡 If you wait a week, you could distort PCC adjustments for that day. 
            <br/><strong>Short version: post today.</strong>
          </p>
        </Section>

        <Section icon="⛳" title="What Scores Should Be Posted?">
          <p style={{ marginBottom: 16 }}>
            You should post any score from a round that:
          </p>
          <CheckItem>Was played under the Rules of Golf</CheckItem>
          <CheckItem>Was played on a rated course</CheckItem>
          <CheckItem>Had at least 9 holes completed</CheckItem>
          
          <p style={{ marginTop: 20, marginBottom: 12 }}><strong>This includes:</strong></p>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
            gap: 8,
            marginBottom: 16
          }}>
            {['Casual rounds', 'Match play', 'Scrambles*', 'Tournament rounds', 'Rounds played away'].map(item => (
              <div key={item} style={{
                background: '#f3f4f6',
                padding: '8px 12px',
                borderRadius: 8,
                fontSize: 14,
                textAlign: 'center'
              }}>
                {item}
              </div>
            ))}
          </div>
          <p style={{ fontSize: 13, color: '#6b7280', fontStyle: 'italic' }}>
            *For scrambles, only if you hole out and have valid individual scores
          </p>
          <p style={{ marginTop: 16, fontWeight: 600, color: '#059669' }}>
            Even if you didn't have your best stuff that day. Especially then. The system is built for it.
          </p>
        </Section>

        <Section icon="📍" title="The Maximum Score You Can Post">
          <p style={{ marginBottom: 16 }}>
            Back in the old days, players used "Equitable Stroke Control" (ESC). That's gone now.
          </p>
          <p style={{ marginBottom: 16 }}>
            Under WHS, the max score you can post on any hole is:
          </p>
          <div style={{
            background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
            color: '#fff',
            padding: 20,
            borderRadius: 12,
            textAlign: 'center',
            marginBottom: 20
          }}>
            <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 4 }}>Net Double Bogey</div>
            <div style={{ opacity: 0.9 }}>Par + 2 + any Course Handicap strokes you receive on that hole</div>
          </div>
          
          <p style={{ marginBottom: 12 }}><strong>Examples:</strong></p>
          <div style={{ 
            background: '#f9fafb', 
            borderRadius: 12, 
            overflow: 'hidden',
            border: '1px solid #e5e7eb'
          }}>
            {[
              { hole: 'Par 4 (no Course HCP stroke)', calc: '4 + 2 = 6', max: '6' },
              { hole: 'Par 4 (1 Course HCP stroke)', calc: '4 + 2 + 1 = 7', max: '7' },
              { hole: 'Par 5 (2 Course HCP strokes)', calc: '5 + 2 + 2 = 9', max: '9' }
            ].map((row, idx) => (
              <div key={idx} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderBottom: idx < 2 ? '1px solid #e5e7eb' : 'none'
              }}>
                <span style={{ flex: 1 }}>{row.hole}</span>
                <span style={{ color: '#6b7280', fontSize: 14 }}>{row.calc}</span>
                <span style={{ 
                  background: '#2d6a4f', 
                  color: '#fff', 
                  padding: '4px 12px', 
                  borderRadius: 6,
                  fontWeight: 700,
                  marginLeft: 16
                }}>
                  Max: {row.max}
                </span>
              </div>
            ))}
          </div>
          <p style={{ marginTop: 16, fontStyle: 'italic', color: '#6b7280' }}>
            If you blow up a hole and make a snowman with extra accessories, just cap it at Net Double Bogey for posting purposes.
          </p>
          <p style={{ 
            marginTop: 16, 
            background: '#dbeafe', 
            padding: 16, 
            borderRadius: 12,
            border: '1px solid #93c5fd'
          }}>
            💡 <strong>Pro tip:</strong> Don't want to do the math? Post your score hole-by-hole in the GHIN app and it will automatically adjust any hole that exceeds Net Double Bogey.
          </p>
        </Section>

        <Section icon="🧮" title="How Many Scores Count?">
          <p style={{ marginBottom: 16 }}>
            Handicaps are no longer "the average of your best 10."
          </p>
          <div style={{
            background: '#f0fdf4',
            border: '2px solid #86efac',
            padding: 20,
            borderRadius: 12,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#166534' }}>
              Under WHS, it's the average of your <Highlight>best 8 out of your last 20</Highlight>
            </div>
          </div>
          <p style={{ marginTop: 16 }}>
            This gives a surprisingly accurate snapshot of your <em>demonstrated potential</em>, 
            not your Sunday meltdown potential.
          </p>
        </Section>

        <Section icon="🔄" title="Handicap Index vs. Course Handicap: What's the Difference?">
          <p style={{ marginBottom: 16 }}>
            These two terms get confused constantly, but they're actually quite different — and understanding 
            the difference will make you sound like a genius at the bar.
          </p>
          
          <div style={{
            display: 'grid',
            gap: 16,
            marginBottom: 20
          }}>
            {/* Handicap Index */}
            <div style={{
              background: '#f0fdf4',
              border: '2px solid #86efac',
              padding: 20,
              borderRadius: 12
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#166534', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24 }}>📊</span> Handicap Index
              </div>
              <p style={{ marginBottom: 12 }}>
                Your <Highlight>Handicap Index</Highlight> is a portable, universal measure of your golfing potential. 
                It's calculated using the <strong>best 8 of your last 20 rounds</strong>, adjusted for course difficulty.
              </p>
              <p style={{ marginBottom: 12 }}>
                This number travels with you and remains comparable at any course in the world. Think of it 
                as your "golf credit score" — a standardized snapshot of how well you can play.
              </p>
              <div style={{
                background: '#dcfce7',
                padding: 12,
                borderRadius: 8,
                fontStyle: 'italic',
                color: '#166534'
              }}>
                💡 Your Index is <strong>not</strong> the number of strokes you get at the course you're playing today — 
                it's the starting point for calculating that number.
              </div>
            </div>

            {/* Course Handicap */}
            <div style={{
              background: '#dbeafe',
              border: '2px solid #93c5fd',
              padding: 20,
              borderRadius: 12
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1e40af', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 24 }}>⛳</span> Course Handicap
              </div>
              <p style={{ marginBottom: 12 }}>
                Your <Highlight>Course Handicap</Highlight> converts your Handicap Index into the actual number 
                of strokes you receive (or give) for a <strong>specific course and set of tees</strong>.
              </p>
              <p style={{ marginBottom: 12 }}>
                This calculation uses the tee's <strong>Course Rating</strong>, <strong>Slope Rating</strong>, and <strong>Par</strong> to 
                reflect how difficult those tees play for the average golfer.
              </p>
              <div style={{
                background: '#bfdbfe',
                padding: 12,
                borderRadius: 8,
                fontStyle: 'italic',
                color: '#1e40af'
              }}>
                💡 Your Course Handicap tells you exactly how many strokes you get (or give) for <em>that round, 
                on that course, from those tees</em>.
              </div>
            </div>
          </div>

          <div style={{
            background: '#fff7ed',
            border: '1px solid #fed7aa',
            padding: 16,
            borderRadius: 12,
            marginBottom: 16
          }}>
            <div style={{ fontWeight: 700, color: '#c2410c', marginBottom: 8 }}>📝 Quick Example:</div>
            <p style={{ margin: 0 }}>
              You have a <strong>12.4 Handicap Index</strong>. At your home course from the white tees, 
              your Course Handicap might be <strong>14</strong>. But when you play from the tips at a harder 
              course with a higher Slope, your Course Handicap could jump to <strong>16 or 17</strong>.
            </p>
          </div>

          <p style={{ fontStyle: 'italic', color: '#059669' }}>
            Same golfer, same index — but the strokes you receive adjust based on where you're playing.
            That's the beauty of the system.
          </p>
        </Section>

        <Section icon="🌡️" title="PCC — The Weather & Course Difficulty Adjuster">
          <p style={{ marginBottom: 16 }}>
            If your group played during:
          </p>
          <div style={{ marginLeft: 16, marginBottom: 16 }}>
            • 30 mph winds<br/>
            • Sideways rain<br/>
            • Firm greens with hole locations chosen by a sociopath
          </div>
          <p style={{ marginBottom: 16 }}>
            The WHS allows for a <Highlight>Playing Conditions Calculation (PCC)</Highlight> to tweak 
            the daily scoring difficulty.
          </p>
          <p style={{
            background: '#dbeafe',
            padding: 16,
            borderRadius: 12,
            border: '1px solid #93c5fd'
          }}>
            💡 You don't have to do anything — it's automatic — but only if everyone posts their scores that day, 
            which is another reason posting matters.
          </p>
        </Section>

        <Section icon="🌍" title="Away Games Count Too">
          <p style={{ marginBottom: 16 }}>
            Travel golf? Tournament trip? Weekend with buddies?
          </p>
          <div style={{
            fontSize: 24,
            fontWeight: 800,
            color: '#2d6a4f',
            textAlign: 'center',
            padding: 16
          }}>
            Post it! 📮
          </div>
          <p>
            Your handicap travels with you, and rated courses worldwide contribute to your scoring record.
            <br/><br/>
            <em>That's part of the magic.</em>
          </p>
        </Section>

        <Section icon="🚩" title="A Few Common Myths">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { myth: '"Match play doesn\'t count."', truth: 'Yes, it does — as long as you hole out or can determine a valid score.' },
              { myth: '"I was just practicing."', truth: 'If you were keeping score and playing holes, that\'s golf. Post it.' },
              { myth: '"I only post tournaments."', truth: 'That\'s how you get a vanity handicap — which usually ends badly for you.' },
              { myth: '"I\'ll post later."', truth: 'Posting the same day keeps PCC accurate and protects everyone\'s handicap.' }
            ].map((item, idx) => (
              <div key={idx} style={{
                background: '#fff7ed',
                border: '1px solid #fed7aa',
                borderRadius: 12,
                padding: 16
              }}>
                <div style={{ fontWeight: 700, color: '#c2410c', marginBottom: 6 }}>{item.myth}</div>
                <div style={{ color: '#1f2937' }}>{item.truth}</div>
              </div>
            ))}
          </div>
        </Section>

        <Section icon="🤝" title="Handicaps Make Golf Fun">
          <p style={{ marginBottom: 16 }}>
            Whether you're playing for:
          </p>
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 8, 
            marginBottom: 20 
          }}>
            {['Bragging rights', '$10 nassaus', 'Club championships', 'Skins', 'Beating your brother-in-law'].map(item => (
              <span key={item} style={{
                background: '#f3f4f6',
                padding: '6px 14px',
                borderRadius: 20,
                fontSize: 14
              }}>
                {item}
              </span>
            ))}
          </div>
          <p style={{ marginBottom: 20 }}>
            A well-maintained handicap makes the game fair, competitive, and enjoyable for everyone.
          </p>
          <div style={{
            background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
            color: '#fff',
            padding: 24,
            borderRadius: 16,
            textAlign: 'center'
          }}>
            <div style={{ fontSize: 18, marginBottom: 8 }}>And the best part?</div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>
              It rewards improvement without punishing participation.
            </div>
            <div style={{ marginTop: 8, opacity: 0.9 }}>
              If you want to get better, the handicap will show it. 📈
            </div>
          </div>
        </Section>

        <Section icon="📦" title="Why This Matters for Your Golf Group">
          <p style={{ marginBottom: 16 }}>
            In organized golf groups, handicaps are important because they:
          </p>
          <CheckItem>Let captains balance teams fairly</CheckItem>
          <CheckItem>Let tournament directors flight the field properly</CheckItem>
          <CheckItem>Make groups competitive and fun</CheckItem>
          <CheckItem>Enable net formats and skins games</CheckItem>
          <CheckItem>Allow new and returning players to blend in smoothly</CheckItem>
          <p style={{ marginTop: 20, color: '#059669', fontWeight: 600 }}>
            The more inclusive the game is, the healthier the golf community becomes. 🏌️‍♀️🏌️‍♂️
          </p>
        </Section>

        {/* Post Score CTA */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: 32,
          textAlign: 'center',
          border: '2px solid #2d6a4f',
          boxShadow: '0 4px 12px rgba(45, 106, 79, 0.15)'
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⛳</div>
          <h3 style={{ fontSize: 22, fontWeight: 700, color: '#1f2937', marginBottom: 12 }}>
            Ready to Post Your Score?
          </h3>
          <p style={{ color: '#6b7280', marginBottom: 24, maxWidth: 400, margin: '0 auto 24px' }}>
            Head to GHIN.com to post your round and keep your handicap accurate.
          </p>
          <a
            href="https://www.ghin.com/post-score"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
              color: '#fff',
              padding: '14px 32px',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 16,
              textDecoration: 'none',
              boxShadow: '0 4px 12px rgba(45, 106, 79, 0.3)',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            📝 Post Your Score on GHIN
          </a>
        </div>

        {/* Footer */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: 48, 
          paddingTop: 24, 
          borderTop: '1px solid #e5e7eb',
          color: '#9ca3af',
          fontSize: 14
        }}>
          <p style={{ marginBottom: 8 }}>
            Brought to you by <strong style={{ color: '#2d6a4f' }}>Tee Time Organizer</strong>
          </p>
          <p>
            Making golf group management simple since 2024 ⛳
          </p>
        </div>

      </main>
    </div>
  );
};

export default HandicapGuidePage;
